import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import ListSubheader from "@material-ui/core/ListSubheader";
import { makeStyles } from "@material-ui/core/styles";
import CenterFocusStrongIcon from "@material-ui/icons/CenterFocusStrong";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import RotateRightIcon from "@material-ui/icons/RotateRight";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import React, { useRef, useState } from "react";
import { useTranslate } from "react-admin";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";

import { formatIsoDateTime } from "../common/dateUtils";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		justifyContent: "flex-start",
		backgroundColor: theme.palette.background.paper,
		overflowY: "scroll",
		minHeight: "500px",
	},
	imageList: {
		padding: theme.spacing(1),
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
	},
	imageItemContainer: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		marginBottom: 20,
	},
	imageItem: {
		maxWidth: 250,
		maxHeight: 350,
		objectFit: "scale-down",
		cursor: "pointer",
	},
	icon: {
		color: "rgba(255, 255, 255, 0.54)",
	},
	modal: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	modalContent: {
		backgroundColor: theme.palette.background.paper,
		padding: theme.spacing(2),
		borderRadius: "5px",
		maxWidth: "90vw",
		maxHeight: "90vh",
	},
}));

const Controls = ({ children }) => {
	const { zoomIn, zoomOut, resetTransform } = useControls();

	return (
		<div className="tools">
			<IconButton
				onClick={() => {
					zoomIn();
				}}
			>
				<ZoomInIcon />
			</IconButton>
			<IconButton
				onClick={() => {
					zoomOut();
				}}
			>
				<ZoomOutIcon />
			</IconButton>
			<IconButton
				onClick={() => {
					resetTransform();
				}}
			>
				<CenterFocusStrongIcon />
			</IconButton>
			{children}
		</div>
	);
};

/**
 *
 * @param {HTMLDivElement} element
 */
const getAspectRatio = element => element.offsetWidth / element.offsetHeight;

/**
 *
 * @param {React.ReactNode} children
 */
const ZoomImage = ({ children }) => {
	const [rotation, setRotation] = useState(0);
	const innerItem = useRef(null);

	// Normalize rotation to 0, 90, 180, 270, etc.
	const normalizedRotation = ((rotation % 360) + 360) % 360;

	const aspectRatio =
		innerItem.current && normalizedRotation % 180 > 0 ? getAspectRatio(innerItem.current) : 1;

	return (
		<div style={{ overflow: "hidden", position: "relative", flex: 1, display: "flex" }}>
			<TransformWrapper centerOnInit initialScale={1}>
				{() => (
					<div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
						<Controls>
							<IconButton onClick={() => setRotation(rotation - 90)}>
								<RotateLeftIcon />
							</IconButton>
							<IconButton onClick={() => setRotation(rotation + 90)}>
								<RotateRightIcon />
							</IconButton>
						</Controls>
						<TransformComponent wrapperStyle={{ flex: 1 }}>
							<div
								ref={innerItem}
								style={{
									flex: 1,
									transform: `rotate(${normalizedRotation}deg) scale(${aspectRatio})`,
									transformOrigin: "center center",
								}}
							>
								{children}
							</div>
						</TransformComponent>
					</div>
				)}
			</TransformWrapper>
		</div>
	);
};

const DocumentImageList = ({ documents }) => {
	const classes = useStyles();
	const translate = useTranslate();

	const validDocs = documents.filter(d => d.base64Image);

	return (
		<div className={classes.root}>
			<Box className={classes.imageList}>
				{validDocs
					.filter(d => d.name.includes("front"))
					.map(item => (
						<Box key={item.id} className={classes.imageItemContainer}>
							<ListSubheader component="div">
								{translate("photoControls.frontPics")}
							</ListSubheader>
							<ZoomImage>
								<img
									src={`data:image/jpg;base64,${item.base64Image}`}
									alt={item.name}
									className={classes.imageItem}
								/>
							</ZoomImage>
							<span>{formatIsoDateTime(item.createdAt)}</span>
						</Box>
					))}
			</Box>

			<Box className={classes.imageList}>
				{validDocs
					.filter(d => d.name.includes("back"))
					.map(item => (
						<Box key={item.id} className={classes.imageItemContainer}>
							<ListSubheader component="div">
								{translate("photoControls.backPics")}
							</ListSubheader>
							<ZoomImage>
								<img
									src={`data:image/jpg;base64,${item.base64Image}`}
									alt={item.name}
									className={classes.imageItem}
								/>
							</ZoomImage>
							<span>{formatIsoDateTime(item.createdAt)}</span>
						</Box>
					))}
			</Box>
		</div>
	);
};

export default DocumentImageList;
